import { createContext, ReactNode, useContext } from "react";

import {
  SalesforceChatSupport,
  SalesforceChatSupportContextValue,
  SalesforceConfig,
} from "./SalesforceChatSupport";

export const AuthenticatedSalesforceChatSupportContext = createContext<
  SalesforceChatSupportContextValue | undefined
>(undefined);

interface AuthenticatedSalesforceChatSupportProps {
  children: ReactNode;
  /**
   * Salesforce configuration for authenticated user support chat.
   * If not provided, defaults to environment variables
   * (NEXT_PUBLIC_SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_*)
   */
  config?: SalesforceConfig;
}

/**
 * Salesforce chat support provider for authenticated pages.
 * This provider is independent from the login chat provider and can coexist with it.
 */
export const AuthenticatedSalesforceChatSupport = ({
  children,
  config,
}: AuthenticatedSalesforceChatSupportProps) => {
  const chatConfig: SalesforceConfig = {
    key:
      config?.key ??
      process.env["NEXT_PUBLIC_SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_KEY"] ??
      "",
    siteEndpoint:
      config?.siteEndpoint ??
      process.env[
        "NEXT_PUBLIC_SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_SITE_ENDPOINT"
      ] ??
      "",
    scrt2Url:
      config?.scrt2Url ??
      process.env[
        "NEXT_PUBLIC_SALESFORCE_AUTHENTICATED_SUPPORT_CHAT_SCRT2URL"
      ] ??
      "",
    sessionLabel: config?.sessionLabel ?? "Authenticated_Messaging_Session",
    websiteLabel: config?.websiteLabel ?? "Authenticated Newsela",
    chatActiveKey:
      config?.chatActiveKey ?? "newsela_authenticated_support_chat_active",
  };

  return (
    <SalesforceChatSupport
      context={AuthenticatedSalesforceChatSupportContext}
      config={chatConfig}
    >
      {children}
    </SalesforceChatSupport>
  );
};

AuthenticatedSalesforceChatSupport.displayName =
  "AuthenticatedSalesforceChatSupport";

/**
 * Hook to access the authenticated Salesforce chat support context.
 * Must be used within an AuthenticatedSalesforceChatSupport provider.
 */
export const useAuthenticatedSalesforceChatSupport =
  (): SalesforceChatSupportContextValue => {
    const context = useContext(AuthenticatedSalesforceChatSupportContext);

    if (context === undefined) {
      throw new Error(
        "useAuthenticatedSalesforceChatSupport must be used within an AuthenticatedSalesforceChatSupport provider",
      );
    }

    return context;
  };
